import { useEffect, useRef } from "react";
import { LuLogOut, LuSettings } from "react-icons/lu";
import { useLocation, useNavigate } from "react-router-dom";
import LogoBlack from "../../assets/logo/logo-black.svg?react";
import LogoGray from "../../assets/logo/logo-gray.svg?react";
import MenuBackIconGray from "../../assets/menu-back-icon-gray.svg?react";
import MenuBackIcon from "../../assets/menu-back-icon.svg?react";
import {
  useAuthActions,
  useAuthUser,
  useIsUserSignedIn,
} from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useProjectsListActions } from "../../core_stores/useProjectsListStore";
import {
  useSubscriptionActions,
  useUserSubscription,
} from "../../core_stores/useSubscriptionStore";
import { useTheme } from "../../providers/ThemeProvider";
import { usePromptInputActions } from "../../view_stores/usePromptInputStore";
import {
  SideMenuBehavior,
  useArePersonalSettingsOpen,
  useIsSideMenuOpen,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import PersonalSettings from "../personal_settings/PersonalSettings";
import SideMenuPinBtn from "./components/SideMenuPinBtn";
import SideMenuProjects from "./components/SideMenuProjects";
import SideMenuSearchField from "./components/SideMenuSearchField";
import SideMenuStartNewProjectBtn from "./components/SideMenuStartNewProjectBtn";

export default function SideMenu() {
  const sideMenuBehavior = useSideMenuBehavior();
  const isSideMenuOpen = useIsSideMenuOpen();
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();
  const isCompactView = useIsCompactView();
  const isUserSignedIn = useIsUserSignedIn();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    openPersonalSettings,
    close: closeSideMenu,
    setSelectedProjectId,
    switchBehavior,
    allowClose,
  } = useSideMenuActions();
  const { loadProjects, clear: clearProjectsListStore } =
    useProjectsListActions();
  const { signOut } = useAuthActions();
  const { resolvedTheme } = useTheme();
  const { clear: clearPromptInput } = usePromptInputActions();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  const isProjectPage = location.pathname.startsWith("/project/");

  // Handle scroll lock in compact mode
  useEffect(() => {
    if (isCompactView && (isSideMenuOpen || arePersonalSettingsOpen)) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100vh";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isCompactView, isSideMenuOpen, arePersonalSettingsOpen]);

  useEffect(() => {
    if (isUserSignedIn) {
      loadProjects();
      reloadSubscriptionsInfo();
    } else {
      clearProjectsListStore();
      setSelectedProjectId(null);
    }
  }, [isUserSignedIn]);

  // Handle mouse leaving the viewport to close side menu in AUTO mode
  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      // Check if mouse left through the left edge of the viewport
      if (
        event.clientX < 0 &&
        sideMenuBehavior === SideMenuBehavior.AUTO &&
        isSideMenuOpen &&
        !arePersonalSettingsOpen
      ) {
        closeSideMenu();
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [
    sideMenuBehavior,
    isSideMenuOpen,
    arePersonalSettingsOpen,
    closeSideMenu,
  ]);

  function logOut() {
    signOut();
    closeSideMenu();
    window.dispatchEvent(new Event("darvin-block-reopen-menu"));
  }

  const handleLogoClick = () => {
    if (isProjectPage) {
      window.dispatchEvent(new Event("darvin-block-reopen-menu"));
      closeSideMenu();
      clearPromptInput();
      navigate("/");
    }
  };

  const handleCollapseIconClick = () => {
    if (sideMenuBehavior === SideMenuBehavior.PINNED) {
      switchBehavior();
    }
    window.dispatchEvent(new Event("darvin-block-reopen-menu"));
    closeSideMenu();
  };

  const shouldBeShown =
    isUserSignedIn &&
    (sideMenuBehavior === SideMenuBehavior.PINNED || isSideMenuOpen);

  return (
    <>
      <OutsideContainer />

      <div
        ref={menuRef}
        onMouseEnter={allowClose}
        className={`${isCompactView ? "w-full" : "w-80"} ${
          shouldBeShown ? "translate-x-0" : "-translate-x-[110%]"
        } h-full flex flex-col pt-4 
      fixed inset-y-0 left-0 w-80
        bg-light-surface-secondary dark:bg-dark-surface-secondary 
        transform transition-transform duration-300 ease-in-out z-40 
        shadow-[5px_0_6px_0_rgba(0,0,0,0.25)]`}
      >
        <div
          className="px-4 flex-1 flex flex-col 
      min-h-[400px] overflow-hidden gap-6"
        >
          <div className="flex items-center justify-between">
            {resolvedTheme === "light" ? (
              <MenuBackIcon
                className="w-8 h-8 cursor-pointer"
                onClick={handleCollapseIconClick}
              />
            ) : (
              <MenuBackIconGray
                className="w-8 h-8 cursor-pointer"
                onClick={handleCollapseIconClick}
              />
            )}
            {resolvedTheme === "light" ? (
              <LogoBlack
                className="h-[26px] w-[126px] cursor-pointer"
                onClick={handleLogoClick}
              />
            ) : (
              <LogoGray
                className="h-[26px] w-[126px] cursor-pointer"
                onClick={handleLogoClick}
              />
            )}
          </div>
          <SideMenuStartNewProjectBtn />
          <SideMenuSearchField />
          <SideMenuProjects />
        </div>
        <div
          className="w-full flex flex-col pb-[42px] pt-4 gap-2
      shadow-[0_-2px_16px_0_rgba(200,203,219,1)] dark:shadow-[0_-2px_16px_0_rgba(0,0,0,0.46)]"
        >
          <div className="flex flex-col gap-2 px-2">
            {/* <SideMenuThemeToggle /> */}
            <ActionBtn
              icon={<LuSettings size={30} className="text-[#8B8B8B]" />}
              label="Settings"
              onClick={openPersonalSettings}
              isSelected={arePersonalSettingsOpen}
            />
            <Profile />
            <ActionBtn
              id="side-menu-log-out"
              icon={<LuLogOut size={30} className="text-[#8B8B8B]" />}
              label="Log out"
              onClick={logOut}
            />
          </div>
          {!isCompactView && <SideMenuPinBtn />}
        </div>
      </div>
      <PersonalSettings />
    </>
  );
}

function OutsideContainer() {
  const { close: closeSideMenu, allowClose } = useSideMenuActions();
  const sideMenuBehavior = useSideMenuBehavior();
  const isSideMenuOpen = useIsSideMenuOpen();
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();

  const closeByOutsideMove = () => {
    if (
      sideMenuBehavior === SideMenuBehavior.AUTO &&
      !arePersonalSettingsOpen
    ) {
      closeSideMenu();
    }
  };

  const shouldShowGlobalContainer =
    (sideMenuBehavior === SideMenuBehavior.AUTO && isSideMenuOpen) ||
    (sideMenuBehavior === SideMenuBehavior.PINNED && arePersonalSettingsOpen);

  return (
    <div
      className={`fixed inset-0 w-full h-full z-30 transition-all duration-300 ease-in-out ${
        shouldShowGlobalContainer
          ? "opacity-100 pointer-events-auto"
          : "opacity-0 pointer-events-none"
      } ${arePersonalSettingsOpen ? "bg-black/25 backdrop-blur-sm" : ""}`}
      onMouseEnter={closeByOutsideMove}
      onClick={() => {
        allowClose();
        closeSideMenu();
      }}
    />
  );
}

function ActionBtn({
  id,
  icon,
  label,
  onClick,
  isSelected,
}: {
  id?: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  isSelected?: boolean;
}) {
  return (
    <button
      id={id}
      className={`w-full py-2 px-1 rounded-lg 
        ${isSelected && "bg-[#C6C9D952] dark:bg-[#2A2E34]"}
      hover:bg-light-hover dark:hover:bg-dark-hover 
      cursor-pointer transition-colors`}
      onClick={onClick}
    >
      <div className="grid grid-cols-[34px_1fr] items-center gap-3">
        <div className="flex items-center justify-center">{icon}</div>
        <div className="text-lg text-left">{label}</div>
      </div>
    </button>
  );
}

function Profile() {
  const user = useAuthUser();
  const subscription = useUserSubscription();

  const { openPersonalSettings } = useSideMenuActions();

  return (
    <div
      className="grid grid-cols-[34px_1fr] 
    items-center gap-3 px-1 rounded-lg
    hover:bg-light-hover dark:hover:bg-dark-hover 
      cursor-pointer transition-colors"
      onClick={() => openPersonalSettings("plans")}
    >
      <div className="w-[34px] h-[34px] rounded-full overflow-hidden">
        <img src={user?.photoURL || ""} />
      </div>
      <div className="min-w-0">
        <div className="text-lg whitespace-nowrap overflow-hidden text-ellipsis">
          User: {user?.displayName}
        </div>
        <div>
          {subscription && subscription?.plan !== "Dream" && subscription?.plan}{" "}
          <span className="font-[300]">Personal Plan</span>
        </div>
      </div>
    </div>
  );
}
